import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import InfoHint from './InfoHint';
import StatCard from './StatCard';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { TableValue } from './ui/table-value';
import { fetchAnalyticsClients, fetchAnalyticsClientsStats } from '../api';
import { PANEL_IDS } from '../config/panelRegistry';
import { usePanelSettings } from '../contexts/PanelSettingsContext';
import useLatestRequest from '../hooks/useLatestRequest';
import usePageForScope from '../hooks/usePageForScope';
import { Eye, Phone, Angry } from 'lucide-react';
import { format } from 'date-fns';
import ClientDetailsModal from './ClientDetailsModal';

const PAGE_SIZE = 20;

const moscowDateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : moscowDateTimeFormatter.format(date);
};

const toDateParam = (value) => (value ? format(value, 'yyyy-MM-dd') : undefined);

const Clients = ({ dateRange }) => {
  const panels = usePanelSettings();

  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyComplaints, setOnlyComplaints] = useState(false);
  const [onlyNegativeClients, setOnlyNegativeClients] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  // Ответ на прежний период или поиск, пришедший позже свежего, не должен его затирать.
  const statsRequests = useLatestRequest();
  const clientsRequests = useLatestRequest();

  const dateParams = useMemo(
    () => ({ date_from: toDateParam(dateRange?.from), date_to: toDateParam(dateRange?.to) }),
    [dateRange?.from, dateRange?.to]
  );

  // Новый период — с первой страницы. Поиск и переключатели сбрасывают страницу сами,
  // в своих обработчиках.
  const pageScope = `${dateParams.date_from}|${dateParams.date_to}`;
  const [page, setPage] = usePageForScope(pageScope);

  const loadStats = useCallback(async () => {
    const request = statsRequests.begin();
    try {
      const data = await fetchAnalyticsClientsStats({ ...dateParams });
      if (request.isCurrent()) {
        setStats(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      request.end();
    }
  }, [dateParams, statsRequests]);

  const loadClients = useCallback(async () => {
    const request = clientsRequests.begin();
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchAnalyticsClients({
        ...dateParams,
        search: searchQuery || undefined,
        only_complaints: onlyComplaints || undefined,
        only_negative_clients: onlyNegativeClients || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE
      });
      if (request.isCurrent()) {
        setClients(response.items ?? []);
        setTotal(response.total ?? 0);
      }
    } catch (err) {
      console.error(err);
      if (request.isCurrent()) {
        setError('Не удалось загрузить список клиентов.');
      }
    } finally {
      const isCurrent = request.isCurrent();
      request.end();
      if (isCurrent) {
        setIsLoading(false);
      }
    }
  }, [dateParams, searchQuery, onlyComplaints, onlyNegativeClients, page, clientsRequests]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const statCards = [
    { panelId: PANEL_IDS['ui:clients.stat-total'], value: (stats?.totalClients ?? 0).toLocaleString() },
    { panelId: PANEL_IDS['ui:clients.stat-calls-per-client'], value: stats?.avgCallsPerClient ?? 0 },
    {
      panelId: PANEL_IDS['ui:clients.stat-with-complaints'],
      value: `${stats?.complaintClients ?? 0} (${stats?.complaintClientsPercent ?? 0}%)`
    },
    {
      panelId: PANEL_IDS['ui:clients.stat-repeat'],
      value: `${stats?.repeatClients ?? 0} (${stats?.repeatClientsPercent ?? 0}%)`
    },
    {
      panelId: PANEL_IDS['ui:clients.stat-repeat-complaints'],
      value: (stats?.angryClients ?? 0).toLocaleString()
    }
  ].filter((card) => panels.isVisible(card.panelId));

  return (
    <div className="space-y-6">
      <Card data-panel-id={PANEL_IDS['ui:clients.header']}>
        <CardHeader>
          <div className="flex items-center gap-1.5">
            <CardTitle>{panels.title(PANEL_IDS['ui:clients.header'])}</CardTitle>
            <InfoHint text={panels.description(PANEL_IDS['ui:clients.header'])} label="О разделе" trackId="clients.header.hint" />
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex flex-1 gap-2">
            <Input
              data-ev="clients.search"
              placeholder="Поиск по номеру телефона"
              value={searchQuery}
              onChange={(event) => {
                setPage(1);
                setSearchQuery(event.target.value);
              }}
            />
            <Button variant="outline" data-ev="clients.filters.reset" onClick={() => { setSearchQuery(''); setPage(1); }}>
              Сброс
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="only-complaints"
              data-ev="clients.filters.only_complaints"
              checked={onlyComplaints}
              onCheckedChange={(value) => { setOnlyComplaints(value); setPage(1); }}
            />
            <label htmlFor="only-complaints" className="text-sm font-medium">Только с жалобами</label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="only-negative-clients"
              data-ev="clients.filters.only_repeat_complaints"
              checked={onlyNegativeClients}
              onCheckedChange={(value) => { setOnlyNegativeClients(value); setPage(1); }}
            />
            <label htmlFor="only-negative-clients" className="text-sm font-medium">Только клиенты с повторными жалобами</label>
          </div>
        </CardContent>
      </Card>

      {/* Подписи плиток приходят из реестра панелей; скрытые отсеиваются до рендера. */}
      {statCards.length > 0 && (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {statCards.map((card) => (
            <StatCard key={card.panelId} panelId={card.panelId} title={panels.title(card.panelId)} value={card.value} />
          ))}
        </div>
      )}

      {panels.isVisible(PANEL_IDS['ui:clients.period']) && (
      <Card data-panel-id={PANEL_IDS['ui:clients.period']}>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <CardTitle>{panels.title(PANEL_IDS['ui:clients.period'])}</CardTitle>
          <div className="text-sm text-muted-foreground">
            Страница {page} из {totalPages} &middot; Всего {total.toLocaleString()}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Телефон</TableHead>
                  <TableHead numeric>Звонки</TableHead>
                  <TableHead>Сигналы</TableHead>
                  <TableHead>Последний звонок</TableHead>
                  <TableHead className="text-right">Детали</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Загрузка...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && clients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Нет клиентов за выбранный период.
                    </TableCell>
                  </TableRow>
                )}
                {clients.map((client) => (
                  <TableRow key={client.clientId}>
                    <TableCell className="font-medium">{client.phone || '—'}</TableCell>
                    <TableCell numeric>
                      <TableValue tone="info" icon={Phone}>{client.totalCalls ?? 0}</TableValue>
                    </TableCell>
                    <TableCell>
                      <ClientSignals client={client} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(client.lastCall)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" data-ev="clients.row.details" onClick={() => setSelectedClient(client)} aria-label="Детали">
                        <Eye className="h-4 w-4 sm:mr-2" /> <span className="max-sm:sr-only">Детали</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>
              Показано {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} из {total}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" data-ev="clients.pagination.prev" disabled={page === 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
                Назад
              </Button>
              <Button variant="outline" size="sm" data-ev="clients.pagination.next" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>
                Вперёд
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      <ClientDetailsModal
        client={selectedClient}
        dateRange={dateRange}
        onClose={() => setSelectedClient(null)}
      />
    </div>
  );
};

const ClientSignals = ({ client }) => {
  const hasComplaint = (client.complaintsCount ?? 0) > 0;
  const hasNegative = (client.negativeCount ?? 0) > 0;
  const hasComplaintSignal = hasComplaint || hasNegative;
  const hasComplaintToday = client.complaintToday || client.negativeToday;

  if (!hasComplaintSignal && !client.isAngry) {
    return <span className="text-muted-foreground">—</span>;
  }

  // Смайлик после бейджа: перед ним он сдвигал бейдж, и «Жалобы» соседних строк не стояли вровень.
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {hasComplaintSignal && (
        <Badge
          className={hasComplaintToday
            ? 'bg-red-600 text-white hover:bg-red-600'
            : 'bg-red-100 text-red-700 hover:bg-red-100'}
        >
          Жалоба{hasComplaintToday ? ' · сегодня' : ''}
        </Badge>
      )}
      {client.isAngry && (
        <Angry
          className="h-4 w-4 text-red-600"
          aria-label="Клиент с повторными жалобами"
          title="Клиент с повторными жалобами более чем в один день за период"
        />
      )}
    </div>
  );
};

export default Clients;
